import { useState } from 'react';

import { useMutation } from '@apollo/client';
import { linkFormatter, useTranslation } from '@crehana/i18n';
// biome-ignore lint/style/noRestrictedImports: legacy code - style/noRestrictedImports
import { TextField } from '@crehana/ui';
import { get } from 'lodash';
// biome-ignore lint/style/noRestrictedImports: legacy code - style/noRestrictedImports
import { useForm } from 'react-hook-form';

import adjustColorBrightness from '@/shared/utils/adjustColorBrightness';
import { validations } from '@/views/Auth/constants';
import AUTH_PAGES_RECOVER_PASSWORD_MUTATION from '@/views/Auth/graphql/AuthPagesRecoverPasswordMutation.graphql';
import type { AuthPagesOrgInfoQuery_organizationInfo as OrgInfoType } from '@/views/Auth/graphql/types/AuthPagesOrgInfoQuery';
import type {
  AuthPagesRecoverPasswordMutation,
  AuthPagesRecoverPasswordMutationVariables,
} from '@/views/Auth/graphql/types/AuthPagesRecoverPasswordMutation';
import { getNextUrl } from '@/views/Auth/utils';

type TFormValues = {
  email: string;
};
const RecoverForm = ({
  orgInfo,
  onBack,
}: {
  orgInfo: OrgInfoType;
  onBack?: () => void;
}) => {
  const { t } = useTranslation();
  const primaryColor = orgInfo?.lookAndFeel?.primary?.main || undefined;
  const nextUrl = getNextUrl();
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<TFormValues>();
  const [email, setEmail] = useState('');

  const [recoverPassword, { data: recoverPasswordData }] = useMutation<
    AuthPagesRecoverPasswordMutation,
    AuthPagesRecoverPasswordMutationVariables
  >(AUTH_PAGES_RECOVER_PASSWORD_MUTATION, {
    onCompleted: data => {
      if (!data.recoverPassword?.success) {
        setError('email', {
          type: 'manual',
          message: get(data.recoverPassword?.messages, '[0].message'),
        });
      }
    },
  });

  const onSubmit = (data: TFormValues) => {
    setEmail(data.email);
    return recoverPassword({
      variables: {
        email: data.email,
        nextUrl,
      },
    });
  };

  if (recoverPasswordData?.recoverPassword?.success) {
    return (
      <div className="tw-h-[calc(100dvh-18.6rem)] md:tw-h-auto md:py-56 tw-flex tw-flex-col tw-justify-center">
        <div className="-tw-mt-8 md:-tw-mt-0">
          <h2 className="tw-font-h3 tw-mb-1.2 md:tw-mb-1.6">
            {t('SUCCESS_TITLE')}
          </h2>
          <p className="tw-font-body3 tw-mb-3.2 md:tw-mb-2.4">
            {t('SUCCESS_DESCRIPTION', {
              interpolation: {
                email,
              },
              formatter: (text: string) => linkFormatter({ text }),
            })}
          </p>
          <button
            className="tw-btn-primary-m md:tw-btn-primary-l tw-w-full tw-flex tw-justify-center"
            onClick={onBack}
            type="button"
            css={
              primaryColor &&
              `
          background-color: ${primaryColor};
          &:hover {
            background-color: ${adjustColorBrightness(primaryColor, 30)};
          }`
            }
          >
            {t('BUTTON_SUCCESS_LABEL')}
          </button>
        </div>
      </div>
    );
  }

  // We are using the syntax [&>div>input] to target the input element inside the wrapper
  // that's because the input doesn't have a prop to override it
  const inputWrapperClassName =
    'rounded-10 [&>div>input]:tw-font-body4 md:[&>div>input]:tw-font-body3 [&>div>input]:tw-py-0 md:[&>div>input]:tw-py-1.6 [&&&]:tw-border-neutral-light-500 md:[&&&]:border-dark-gray';

  const inputLabelClassName =
    '[&>span]:tw-font-subtitle5 md:[&>span]:tw-font-subtitle4';

  return (
    <form className="md:pt-32" onSubmit={handleSubmit(onSubmit)}>
      <h2 className="tw-font-h2 tw-mb-1.2">{t('FORM_TITLE')}</h2>
      <p className="tw-font-body3 tw-mb-4 md:tw-mb-2.4">
        {t('FORM_DESCRIPTION')}
      </p>
      <TextField
        className={`tw-mb-3.2 md:tw-mb-2.4 ${inputLabelClassName}`}
        wrapperClassName={`${inputWrapperClassName}`}
        id="email"
        name="email"
        label={t('INPUT_EMAIL_LABEL')}
        hint={t('INPUT_EMAIL_HINT')}
        ref={register({
          required: validations.required(t),
          pattern: validations.email(t),
        })}
        disabled={isSubmitting}
        errorText={errors.email?.message}
        full
      />
      <button
        className="tw-btn-primary-m md:tw-btn-primary-l tw-w-full tw-flex tw-justify-center"
        disabled={isSubmitting}
        type="submit"
        css={
          primaryColor &&
          `
          background-color: ${primaryColor};
          &:hover {
            background-color: ${adjustColorBrightness(primaryColor, 30)};
          }`
        }
      >
        {t(isSubmitting ? 'SENDING' : 'BUTTON_SUBMIT_LABEL')}
      </button>
    </form>
  );
};

export default RecoverForm;
